"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import AppLayout from "../../../navigation/Layout/Layout";
import ScrollTabs from "../../../Profile/components/ScrollTabs/ScrollTabs";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";

import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import usePrescriptions from "../../../prescriptions/hooks/usePrescriptions";
import { fetchPrescriptionLayout } from "../../../prescriptions/redux/thunks/prescriptionLayoutThunks";
import { useTour } from "../../../appointments/hooks/useTour";
import { medicinesTourSteps } from "./medicinesTourConfig";

import MedicationHistoryCard from "../../components/MedicationHistoryCard/MedicationHistoryCard";
import MedicationListCard from "../../components/MedicationListCard/MedicationListCard";
import PrescriptionHistoryCard from "../../components/PrescriptionHistoryCard/PrescriptionHistoryCard";
import PrescriptionModal from "../../components/Prescriptionmodal/Prescriptionmodal";
import AddModal from "../../components/AddModal/AddModal";

import {
  selectMedicationReminder,
} from "../../redux/slices/medicationSlice";

import "./Medicines.css";

function getActiveKey(pathname) {
  const p = (pathname || "").toLowerCase();
  if (p.startsWith(ROUTES.OVERVIEWS.toLowerCase())) return ROUTES.OVERVIEWS;
  if (p.startsWith(ROUTES.MEDICAL_RECORD.toLowerCase())) return ROUTES.MEDICAL_RECORD;
  if (p.startsWith(ROUTES.MEDICINES.toLowerCase())) return ROUTES.MEDICINES;
  if (p.startsWith("/profile")) return ROUTES.PROFILE(":userId");
  return ROUTES.MEDICINES;
}
const normalizeId = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};
export default function Medicines() {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();



  const { effectiveUserId } = useAuthSession({ auto: true });
  const userId = effectiveUserId || null;
  // ✅ vrai doctor depuis redux
  const doctorUser = useSelector((state) => state.doctor?.user);

  const doctorId = useMemo(() => {
    return (
      normalizeId(doctorUser?.id) ||
      normalizeId(doctorUser?.doctorId) ||
      normalizeId(doctorUser?.pgId) ||
      normalizeId(doctorUser?._id) ||
      null
    );
  }, [doctorUser]);
  const {
      prescriptionItems,
    loading: prescriptionsLoading,
    error: prescriptionsError,
    refetch: refetchPrescriptions,
  } = usePrescriptions({
    patientId: userId,
    doctorId: null, 
  });

  const reminder = useSelector(selectMedicationReminder);

  useEffect(() => {
  }, [reminder]);

  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  const activeKey = useMemo(() => getActiveKey(pathname), [pathname]);

  const onTabChange = (key) => {
    if (key.includes(":userId") && userId) navigate(ROUTES.PROFILE(userId));
    else navigate(key);
  };

  useEffect(() => {
    return () => undefined;
  }, [userId, pathname]);

  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "medicines";
    const requestedAction = searchParams.get("action");

    if (requestedAction === "add") {
      setAddOpen(true);
    }

    if (hasGuide || hasTour) {
      setTimeout(() => {
        const medsSection = document.querySelector(".meds-section");
        const medsLeft = document.querySelector(".meds-left");
        const medsRight = document.querySelector(".meds-right");


        if (medsSection && medsLeft && medsRight) setShouldStartTour(true);
        else setTimeout(() => setShouldStartTour(true), 1500);
      }, 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, medicinesTourSteps);

  const handlePrescriptionClick = async (prescriptionId) => {
    try {
      if (!prescriptionItems?.length) return;

      const prescription = prescriptionItems.find((p) => p.id === prescriptionId);
      if (!prescription) return;

      const original = prescription._original || {};
      const embeddedTemplate = original.layout || original.template || {};
      const issuingDoctorId =
        original.medecinId ||
        original.doctorId ||
        original.doctor?.id ||
        original.doctor?._id ||
        null;
      let savedTemplate = null;

      if (issuingDoctorId) {
        try {
          savedTemplate = await dispatch(
            fetchPrescriptionLayout(issuingDoctorId)
          ).unwrap();
        } catch {
          savedTemplate = null;
        }
      }

      const prescriptionContent =
        prescription.name || prescription.content || original.content;

      const doctorInfo = original.doctor || {
        fullName: prescription.doctorName || t("mediciness.defaults.unknownDoctor"),
        specialty:
          original.doctorSpecialty ||
          prescription.type ||
          t("mediciness.defaults.medicalProfessional"),
        phone: original.doctorPhone || original.phone || "",
        email: original.doctorEmail || original.email || "",
        clinicAddress:
          original.clinicAddress || original.address || "",
      };

      const template = {
        ...savedTemplate,
        ...embeddedTemplate,
        ...original,
        logoUrl:
          original.logoUrl || original.logo_url || embeddedTemplate.logoUrl ||
          embeddedTemplate.logo_url || savedTemplate?.logoUrl || null,
        couleurTexte:
          original.couleurTexte || original.couleur_texte || embeddedTemplate.couleurTexte ||
          embeddedTemplate.couleur_texte || savedTemplate?.couleurTexte || null,
        couleurLogo:
          original.couleurLogo || original.couleur_logo || embeddedTemplate.couleurLogo ||
          embeddedTemplate.couleur_logo || savedTemplate?.couleurLogo || null,
        fullName:
          original.fullName || original.full_name || embeddedTemplate.fullName ||
          savedTemplate?.fullName || doctorInfo.fullName,
        fullNameAr:
          original.fullNameAr || original.full_name_ar || embeddedTemplate.fullNameAr ||
          savedTemplate?.fullNameAr || null,
        specialty:
          original.specialty || embeddedTemplate.specialty || savedTemplate?.specialty ||
          doctorInfo.specialty,
        specialtyAr:
          original.specialtyAr || original.specialty_ar || embeddedTemplate.specialtyAr ||
          savedTemplate?.specialtyAr || null,
        phone: original.phone || embeddedTemplate.phone || savedTemplate?.phone || doctorInfo.phone || "",
        email: original.email || embeddedTemplate.email || savedTemplate?.email || doctorInfo.email || "",
        address:
          original.address || original.clinicAddress || embeddedTemplate.address ||
          savedTemplate?.address || doctorInfo.clinicAddress || "",
        addressAr:
          original.addressAr || original.address_ar || embeddedTemplate.addressAr ||
          savedTemplate?.addressAr || "",
      };

      setSelectedPrescription({
        id: prescription.id,
        content: prescriptionContent,
        type: original.documentType || prescription.type,
        doctor: doctorInfo,
        layout: template,
        createdAt: original.createdAt || prescription.dateLabel,
      });

      setPrescriptionModalOpen(true);
    } catch {
    }
  };

  const handleClosePrescriptionModal = () => {
    setPrescriptionModalOpen(false);
    setSelectedPrescription(null);
  };

  useEffect(() => {
    if (prescriptionsError) {
    }
  }, [prescriptionsError]);

  return (
    <AppLayout>
      <div className="profile medicines-page">
        <ScrollTabs activeKey={activeKey} onChange={onTabChange} userId={userId} />

        <div className="meds-inner">
          <div className="meds-container">
            <div className="meds-left">
             
             
             

              <div className="medication-list-card">
                <MedicationListCard
                  title={t("mediciness.left.medicationList.title")}
                  date={new Date()}
                  onEdit={() => setAddOpen(true)}
                  onRemindToggle={(v) =>
                    undefined
                  }
                />

                {/* 🧪 TEST REMINDER BUTTON */}
                {}
              </div>
            </div>

            <aside className="meds-right meds-section">
              <MedicationHistoryCard
                title={t("mediciness.right.medHistory.title")}
                subtitle={t("mediciness.right.medHistory.subtitle")}
                items={[]}
                onSeeAll={() => navigate("/medicines/history")}
                placeholder={t("mediciness.right.medHistory.placeholder")}
              />

              <PrescriptionHistoryCard
                title="Medical Document History"
                subtitle="All medical documents issued by your doctors."
                items={prescriptionItems || []}
                loading={prescriptionsLoading}
                onPrescriptionClick={handlePrescriptionClick}
                placeholder="Search your medical documents"
              />
            </aside>
          </div>
        </div>

        <AddModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onCreateMedication={() => {
            setAddOpen(false);
            refetchPrescriptions();
          }}
        />

        <PrescriptionModal
          open={prescriptionModalOpen}
          onClose={handleClosePrescriptionModal}
          prescriptionData={selectedPrescription}
        />
      </div>
    </AppLayout>
  );
}
